import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LayoutService, NavbarBtn } from 'src/app/layout/layout.service';
import { ThirdPartyService } from '../../third-party.service';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, delay, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { isDate } from 'moment';
import * as moment from 'moment';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
  selector: 'app-shop-report',
  templateUrl: './shop-report.component.html',
  styleUrls: ['./shop-report.component.scss']
})
export class ShopReportComponent {
  @ViewChild('shopDropdown') shopDropdown!: NgSelectComponent;
  loading: boolean = true;
  summaryLoading: boolean = true;
  outstandingReportData: any[] = [];
  isSummaryExpanded: boolean = false;
  summary: any;
  isExpandFilter: boolean = true;
  is_openSearch: boolean = false;
  subscription = new Subscription;
  searchKeyword: string = '';
  searchForm: FormGroup;
  filterForm: FormGroup;
  isSearchActive: boolean = false;
  firstSelectionDone: boolean = false;

  shopList: any[] = [];
  isShopListLoading: boolean = true;

  params: {
    skip: number;
    take: number;
    takeAll: boolean;
    isDateChecked: boolean;
    fromDate: string | number | null;
    toDate: string | number | null;
    key: string;
    shopId: number;
  } = {
      skip: 0,
      take: 50,
      isDateChecked: false,
      fromDate: null,
      toDate: null,
      key: '',
      shopId: 0,
      takeAll: true,
    };

  // lazy load vriables
  throttle = 300;
  scrollDistance = 3;
  scrollUpDistance = 1.5;
  isEndOfList = false;
  isLazyloading: boolean = false;

  constructor(
    public layoutService: LayoutService,
    public thirdPartyService: ThirdPartyService,
    private fb: FormBuilder,
  ) { }

  ngOnInit(): void {
    this.getStaffIdAndBranchId();
    this.populateToolbarData();
    this.setSearchForm();
    this.setFilterForms();
    // this.getOutstandingReport();

    setTimeout(() => {
      if (this.shopDropdown) {
        this.shopDropdown.open();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onScroll() {
    if (!this.isLazyloading && !this.isEndOfList) {
      this.isLazyloading = true;
      this.params.skip += this.params.take;
      this.getOutstandingReport();

    }
  }

  public toggleSummary() {
    this.isSummaryExpanded = !this.isSummaryExpanded;
  }

  public clearSearch() {
    this.params.key = "";
    const searchValue = this.searchForm.get('search')?.value;
    this.searchForm.get('search')?.setValue('');
    if (searchValue) {
      this.getOutstandingReport();
    }
  }

  public applyFilter() {
    this.params.shopId = this.filterForm.get('shop')?.value?.SalesPOSUserID || 0;
    this.params.fromDate = moment(this.filterForm.get('fromDate')?.value).startOf("day").unix();
    this.params.toDate = moment(this.filterForm.get('toDate')?.value).startOf("day").unix();

    this.getOutstandingReport();
  }

  public clearFilter() {
    this.setFilterForms();
    this.params.shopId = 0;
    this.getOutstandingReport();
  }

  public parseUnixTimestamp(unixTimestamp: any) {
    const m = moment.unix(Number(unixTimestamp));
    return {
      day: m.date(),                  // 1–31
      month: m.month() + 1,           // 0-based in moment → +1
      year: m.year(),                 // YYYY
      time: m.format("hh:mm A"),      // 12-hour format with AM/PM
      hour: m.format("hh"),           // hour (12-hour)
      minute: m.format("mm"),         // minute
      ampm: m.format("A"),            // AM or PM
      fullDate: m.format("DD-MM-YYYY"), // optional
      fullTime12: m.format("hh:mm:ss A"), // e.g., 03:27:45 PM
    };
  }

  public onShopSelected(id: any) {
    this.params.shopId = this.filterForm.get('shop')?.value?.SalesPOSUserID || 0;
    if (!this.firstSelectionDone) {
      this.firstSelectionDone = true;
      this.getOutstandingReport();
    }
  }

  private populateToolbarData() {
    this.layoutService.setToolbar({
      title: 'Shop Report',
    });

    this.layoutService.setNavbarButtons([
      { action: 'search', state: null, icon: 'search' },
      { action: 'filter', state: null, icon: 'filter_list' },
    ]);

    this.subscription.add(
      this.layoutService.buttonClicked$.subscribe((btn: NavbarBtn) => {

        if (!btn) return;

        switch (btn.action) {
          case 'filter':
            this.isExpandFilter = !this.isExpandFilter;
            break;
          case 'search':
            this.is_openSearch = !this.is_openSearch;
            break;
        }
      })
    );
  }

  private setSearchForm() {
    this.searchForm = this.fb.group({
      search: [""]
    });

    this.subscription.add(this.searchForm.controls['search'].valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.isSearchActive = true),
      delay(300),
      switchMap((str: string) => {
        this.params.key = str;
        this.params.skip = 0;
        return this.thirdPartyService.getOutstandingReport(this.params);

      })).subscribe((res: any) => {

        this.isSearchActive = false;
        this.outstandingReportData = res.ResponseData;
        this.getOutstandingReportSummary();
      }));
  }

  private setFilterForms() {
    const today = new Date();

    // 1st day of current month (local date)
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);

    // End of today
    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);

    this.params.isDateChecked = true;
    this.params.fromDate = moment(startOfMonth).startOf('day').unix();
    this.params.toDate = moment(endOfToday).endOf('day').unix();

    this.filterForm = this.fb.group({
      fromDate: [this.formatDateForInput(startOfMonth)],
      toDate: [this.formatDateForInput(endOfToday)],
      shop: [null],
    });
  }

  private getOutstandingReport() {
    this.loading = true;
    this.thirdPartyService.getOutstandingReport(this.params).subscribe({
      next: (res: any) => {
        this.isLazyloading = false;
        this.outstandingReportData = res.ResponseData;
        this.getOutstandingReportSummary();
        if (res.ResponseData.length < this.params.take) {
          this.isEndOfList = true;
        }
        this.loading = false;
      },
      error: (err: any) => {
        this.loading = false;
      }
    });
  }

  private getOutstandingReportSummary() {
    this.summaryLoading = true;
    this.thirdPartyService.getOutstandingReportSummary(this.params).subscribe({
      next: (res: any) => {
        if (res?.Result === 'Success' && res?.ResponseData) {
          this.summary = res.ResponseData;
          this.summaryLoading = false;
        }
        this.summaryLoading = false;
      },
      error: (err) => {
        console.error('Error fetching service summary:', err);
        this.summaryLoading = false;
      },
    });
  }

  private getStaffIdAndBranchId() {
    this.thirdPartyService.getStaffIdAndBrandchId().subscribe((res: any) => {
      if (res?.Result === 'Success' && res?.ResponseData) {
        let branchInfo = res.ResponseData;
        this.getShops(branchInfo);
      }
    });
  }

  public getShops(branchInfo: any) {
    this.isShopListLoading = true;
    this.thirdPartyService.getShops(branchInfo.UserId).subscribe(
      (res: any) => {
        if (res && res?.Result == 'Success' && res?.ResponseData != 'Failed') {
          this.shopList = res?.ResponseData;
        }
        this.isShopListLoading = false;
      },
      (error: HttpErrorResponse) => {
        this.isShopListLoading = false;
       }
    );
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;   // Local date — no timezone conversion
  }

}
